/**
 * アプリケーション全体の有限状態機械（TK-013、FR-012 / FR-013、`04-architecture.md` game）。
 *
 * ```text
 *   AGE_GATE ──認証──▶ PLAYING_SECRET ──秘文字成立──▶ SECRET_RESOLVING
 *                            ▲                              │ (フェードアウト)
 *                            └────────────────────── PLAYING_IMAGE
 *                                                           │ (画像成立)
 *                                                           ▼
 *                                                       CLEARING ──演出完了──▶ CLEARED
 * ```
 *
 * 段階遷移（`04a` §5、ADR-005）は **入力停止 → 秘文字フェードアウト → `target` を `τ` から
 * `id` へ切替 → `computeGroups` 再実行 → 入力再開** の順で行う。グループは導出量であるため
 * （ADR-010）、明示的な全解除処理は存在しない — `target` の切り替えと再算出だけで、`τ` 基準の
 * 結合は自動的に消える。配置はそのまま維持され、再シャッフルしない。
 *
 * DOM・時刻・演出には直接触れず、描画とスケジューラを注入で受け取る。状態遷移の副作用は
 * `core` に持ち込まない。
 */

import { assertInvariants, positionsOf } from '../core/board';
import { computeGroups } from '../core/groups';
import { IDENTITY_TARGET, isSolved } from '../core/stage';
import type { PieceId, Placement, TargetLayout } from '../core/types';
import type { BoardRenderState } from '../view/board-view';

/** ゲームの状態。 */
export type GameState =
  | 'AGE_GATE'
  | 'PLAYING_SECRET'
  | 'SECRET_RESOLVING'
  | 'PLAYING_IMAGE'
  | 'CLEARING'
  | 'CLEARED';

/** 描画時の付帯情報。押しのけアニメーション（FR-020、TK-012）と結合フラッシュが使う。 */
export interface RenderOptions {
  /** このドロップで移動アニメーションの対象になるピース（押しのけ + 置いたグループ）。 */
  readonly displaced: readonly PieceId[];
  /**
   * このドロップでドラッグされていたグループ（TK-052）。ドロップで直接できた結合を
   * 見分けるために view が使う。
   */
  readonly dragged?: readonly PieceId[];
}

/** 状態機械の依存。 */
export interface GameDeps {
  readonly C: number;
  readonly R: number;
  /** 秘文字の目標配置 `τ`。 */
  readonly tau: TargetLayout;
  /** 初期配置（`core/shuffle.initialPlacement` の結果）。 */
  readonly initialPlacement: Placement;
  /** 盤面の描画（`view/board-view.render`）。 */
  readonly render: (state: BoardRenderState, options?: RenderOptions) => void;
  /** 秘文字レイヤーの表示切替（`view/board-view.setSecretVisible`）。 */
  readonly setSecretVisible: (visible: boolean) => void;
  /** 秘文字フェードアウトの所要時間（`view/constants.SECRET_FADE_MS`）。 */
  readonly secretFadeMs: number;
  /** 遅延実行。省略時は `setTimeout`。テストは手動スケジューラを注入する。 */
  readonly schedule?: (fn: () => void, ms: number) => void;
  /** 状態遷移の通知。shell が `data-game-state` の反映等に使う。 */
  readonly onStateChange?: (state: GameState) => void;
  /**
   * クリア演出（`view/effects`、TK-014）。resolve で `CLEARED` へ進む。
   * 省略時は演出なしで即座に `CLEARED` になる。
   */
  readonly runClearEffects?: () => Promise<void>;
}

/** 状態機械のハンドル。 */
export interface Game {
  /** 現在の状態。 */
  readonly state: () => GameState;
  /** ドラッグが参照する盤面の現在状態（`controller/drag.DragBoardState` 互換）。 */
  readonly board: () => {
    placement: Placement;
    target: TargetLayout;
    C: number;
    R: number;
  };
  /** ドラッグ入力を受け付けるか。`SECRET_RESOLVING` と `CLEARING` の間は受け付けない。 */
  readonly dragEnabled: () => boolean;
  /** 年齢認証の完了。`AGE_GATE` から `PLAYING_SECRET` へ進む。 */
  readonly start: () => void;
  /** ドロップ確定。新しい配置と押しのけられたピースを受け取り、成立判定まで行う。 */
  readonly commitDrop: (
    placement: Placement,
    displaced?: readonly PieceId[],
    dragged?: readonly PieceId[],
  ) => void;
}

export function createGame(deps: GameDeps): Game {
  const { C, R } = deps;
  const N = C * R;
  const schedule = deps.schedule ?? ((fn, ms) => void setTimeout(fn, ms));

  let state: GameState = 'AGE_GATE';
  let placement: Placement = [...deps.initialPlacement];
  let target: TargetLayout = deps.tau;
  let phase: 1 | 2 = 1;

  const setState = (next: GameState): void => {
    state = next;
    deps.onStateChange?.(next);
  };

  const render = (options?: RenderOptions): void => {
    deps.render({ placement, groups: computeGroups(placement, target, C), phase }, options);
  };

  /** 段階遷移（`04a` §5）。入力は `SECRET_RESOLVING` の間止まる。 */
  const advanceStage = (): void => {
    setState('SECRET_RESOLVING');
    deps.setSecretVisible(false);
    schedule(() => {
      // target の差し替えと再描画だけで、τ 基準の結合は消え id 基準で数え直される。
      // 配置には触れない（再シャッフルしない）。
      target = IDENTITY_TARGET(N);
      phase = 2;
      render();
      setState('PLAYING_IMAGE');
    }, deps.secretFadeMs);
  };

  /** クリア（FR-014 への接続点）。演出の完了で `CLEARED` になる。 */
  const clear = (): void => {
    setState('CLEARING');
    (deps.runClearEffects?.() ?? Promise.resolve()).then(() => setState('CLEARED'));
  };

  // 盤面は年齢認証の解決を待たずに描く。認証前の遮蔽は age-gate の責務であり
  //（ブラー + マスク + inert）、盤面の存在自体は隠さない（ADR-003 / FR-002）。
  render();

  return {
    state: () => state,
    board: () => ({ placement, target, C, R }),
    dragEnabled: () => state === 'PLAYING_SECRET' || state === 'PLAYING_IMAGE',
    start: () => {
      if (state === 'AGE_GATE') setState('PLAYING_SECRET');
    },
    commitDrop: (next, displaced = [], dragged = []) => {
      // 演出中・認証前のドロップは受け付けない（FR-013 / FR-014）。drag 側も
      // `dragEnabled` で止めるが、時間差で届いた確定をここで最終的に落とす。
      if (state !== 'PLAYING_SECRET' && state !== 'PLAYING_IMAGE') return;

      placement = next;
      // ドロップ確定のたびに不変条件を検証する（`04a` §8）。本番ビルドでは消える。
      assertInvariants({ C, R, placement, target });
      render({ displaced, dragged });

      const positionOf = positionsOf(placement);
      if (state === 'PLAYING_SECRET' && isSolved(positionOf, target)) advanceStage();
      else if (state === 'PLAYING_IMAGE' && isSolved(positionOf, target)) clear();
    },
  };
}
